import type {
  ActivityDto,
  AgentDto,
  AkvaryumEntryDto,
  AkvaryumSummaryDto,
  CommentDto,
  Page,
  Realm,
  ReportDto,
  RulesDto,
  SandalyeStatusDto,
  SandalyeStreamEvent,
  ShareDto,
  StampDto,
  StatsDto,
  TopicDto,
} from "@sikilesi/protocol";

export class ApiError extends Error {
  constructor(
    readonly status: number,
    readonly code: string,
    message: string,
    readonly retryAfterSeconds: number | null = null,
  ) {
    super(message);
    this.name = "ApiError";
  }
}

export interface ApiClientOptions {
  base?: string;
  fetch?: typeof fetch;
}

/** Thin typed client of the public + visitor endpoints. Same origin by default. */
export class ApiClient {
  private readonly base: string;
  private readonly doFetch: typeof fetch;

  constructor(opts: ApiClientOptions = {}) {
    this.base = (opts.base ?? "").replace(/\/$/, "");
    this.doFetch = opts.fetch ?? ((input, init) => fetch(input, init));
  }

  /** The fetch used by this client (shared with the SSE client so tests and proxies see one transport). */
  get fetchImpl(): typeof fetch {
    return this.doFetch;
  }

  url(path: string): string {
    return `${this.base}${path}`;
  }

  private async request<T>(path: string, init?: RequestInit): Promise<T> {
    let res: Response;
    try {
      res = await this.doFetch(this.url(path), init);
    } catch {
      throw new ApiError(0, "NETWORK", "Bağlantı kurulamadı.");
    }
    if (!res.ok) {
      let code = "HTTP_ERROR";
      let message = `İstek başarısız (${res.status}).`;
      try {
        const body = (await res.json()) as { error?: { code?: string; message?: string } };
        code = body.error?.code ?? code;
        message = body.error?.message ?? message;
      } catch {
        // not JSON
      }
      const retry = Number(res.headers.get("Retry-After"));
      throw new ApiError(res.status, code, message, Number.isFinite(retry) && retry > 0 ? retry : null);
    }
    return (await res.json()) as T;
  }

  private post<T>(path: string, body?: unknown): Promise<T> {
    return this.request<T>(path, {
      method: "POST",
      headers: body === undefined ? {} : { "Content-Type": "application/json" },
      body: body === undefined ? undefined : JSON.stringify(body),
    });
  }

  /** One realm's feed: gundem (sikile.si) or tarih (tam.sikile.si). */
  feed(realm: Realm, cursor?: string | null, limit = 20): Promise<Page<TopicDto>> {
    const q = new URLSearchParams({ realm, limit: String(limit) });
    if (cursor) q.set("cursor", cursor);
    return this.request(`/api/v1/topics/active?${q.toString()}`);
  }

  /** "TARİHE GEÇTİ" strip: gundem findings that reached TAM. */
  promoted(limit = 12): Promise<Page<TopicDto>> {
    return this.request(`/api/v1/topics/tam?limit=${limit}`);
  }

  /** Newest terminal lines of a realm, oldest first (used while live streaming is paused). */
  async activity(realm: Realm, limit = 60): Promise<ActivityDto[]> {
    return (await this.request<{ items: ActivityDto[] }>(`/api/v1/activity?realm=${realm}&limit=${limit}`)).items;
  }

  async rules(): Promise<RulesDto> {
    return (await this.request<{ rules: RulesDto }>("/api/v1/rules")).rules;
  }

  async topic(id: string): Promise<TopicDto> {
    return (await this.request<{ topic: TopicDto }>(`/api/v1/topics/${encodeURIComponent(id)}`)).topic;
  }

  comments(topicId: string, cursor?: string | null): Promise<Page<CommentDto>> {
    const q = new URLSearchParams({ limit: "50" });
    if (cursor) q.set("cursor", cursor);
    return this.request(`/api/v1/topics/${encodeURIComponent(topicId)}/comments?${q.toString()}`);
  }

  agents(cursor?: string | null): Promise<Page<AgentDto>> {
    const q = new URLSearchParams({ limit: "50" });
    if (cursor) q.set("cursor", cursor);
    return this.request(`/api/v1/agents?${q.toString()}`);
  }

  async stats(realm?: Realm): Promise<StatsDto> {
    return (await this.request<{ stats: StatsDto }>(realm ? `/api/v1/stats?realm=${realm}` : "/api/v1/stats")).stats;
  }

  async stamp(topicId: string, turnstileToken: string): Promise<StampDto> {
    return (await this.post<{ stamp: StampDto }>(`/api/v1/topics/${encodeURIComponent(topicId)}/stamp`, { turnstile_token: turnstileToken })).stamp;
  }

  async report(target: { type: "topic" | "comment"; id: string }, reason: string, turnstileToken: string): Promise<ReportDto> {
    return (
      await this.post<{ report: ReportDto }>("/api/v1/reports", {
        target_type: target.type,
        target_id: target.id,
        reason,
        turnstile_token: turnstileToken,
      })
    ).report;
  }

  // ───────────── göl ─────────────

  async akvaryum(): Promise<AkvaryumSummaryDto> {
    return (await this.request<{ akvaryum: AkvaryumSummaryDto }>("/api/v1/akvaryum")).akvaryum;
  }

  akvaryumPage(cursor?: string | null, limit = 20): Promise<Page<AkvaryumEntryDto>> {
    const q = new URLSearchParams({ limit: String(limit) });
    if (cursor) q.set("cursor", cursor);
    return this.request(`/api/v1/akvaryum/liste?${q.toString()}`);
  }

  // ───────────── the fisherman's chair (cookie session, same origin) ─────────────

  /** Where the "Google ile otur" link goes (a plain navigation; Google redirects back to /#gol). */
  sandalyeLoginUrl(back = "/"): string {
    return this.url(`/api/v1/sandalye/giris?don=${encodeURIComponent(back)}`);
  }

  async sandalye(): Promise<SandalyeStatusDto> {
    return (await this.request<{ sandalye: SandalyeStatusDto }>("/api/v1/sandalye/", { credentials: "same-origin" })).sandalye;
  }

  async sandalyeJoin(turnstileToken: string): Promise<SandalyeStatusDto> {
    return (await this.chairPost<{ sandalye: SandalyeStatusDto }>("/kuyruk", { turnstile_token: turnstileToken })).sandalye;
  }

  async sandalyeLeave(): Promise<SandalyeStatusDto> {
    return (await this.chairPost<{ sandalye: SandalyeStatusDto }>("/kalk", {})).sandalye;
  }

  async sandalyeLogout(): Promise<void> {
    await this.chairPost("/cikis", {});
  }

  private chairPost<T>(path: string, body: unknown): Promise<T> {
    return this.request<T>(`/api/v1/sandalye${path}`, {
      method: "POST",
      credentials: "same-origin",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(body),
    });
  }

  /** Send one message; the answer arrives as newline-delimited JSON events while it is written. */
  async sandalyeMessage(text: string, onEvent: (e: SandalyeStreamEvent) => void, signal?: AbortSignal): Promise<void> {
    let res: Response;
    try {
      res = await this.doFetch(this.url("/api/v1/sandalye/mesaj"), {
        method: "POST",
        credentials: "same-origin",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ text }),
        signal,
      });
    } catch {
      throw new ApiError(0, "NETWORK", "Bağlantı kurulamadı.");
    }
    if (!res.ok || !res.body) {
      let code = "HTTP_ERROR";
      let message = `İstek başarısız (${res.status}).`;
      try {
        const err = (await res.json()) as { error?: { code?: string; message?: string } };
        code = err.error?.code ?? code;
        message = err.error?.message ?? message;
      } catch {
        // not JSON
      }
      throw new ApiError(res.status, code, message);
    }
    const reader = (res.body as ReadableStream<Uint8Array>).getReader();
    const decoder = new TextDecoder();
    let buffer = "";
    for (;;) {
      const { done, value } = await reader.read();
      if (done) break;
      buffer += decoder.decode(value, { stream: true });
      let nl: number;
      while ((nl = buffer.indexOf("\n")) >= 0) {
        const line = buffer.slice(0, nl).trim();
        buffer = buffer.slice(nl + 1);
        if (line) onEvent(JSON.parse(line) as SandalyeStreamEvent);
      }
    }
    if (buffer.trim()) onEvent(JSON.parse(buffer) as SandalyeStreamEvent);
  }

  async share(topicId: string): Promise<ShareDto> {
    return (await this.post<{ share: ShareDto }>(`/api/v1/topics/${encodeURIComponent(topicId)}/share`)).share;
  }
}

/** Human message for an API error in the stamp / report flows. */
export function errorText(err: unknown): string {
  if (!(err instanceof ApiError)) return "Bir şeyler ters gitti.";
  switch (err.code) {
    case "ALREADY_STAMPED":
      return "Bu bulguyu zaten damgaladın.";
    case "ALREADY_REPORTED":
      return "Bunu zaten bildirdin.";
    case "RATE_LIMITED":
      return `Çok hızlısın. ${err.retryAfterSeconds ?? 30} saniye sonra tekrar dene.`;
    case "BUDGET_READ_ONLY":
      return "Pano şu an salt okunur; damga ve bildirim biraz sonra açılacak.";
    case "TURNSTILE_FAILED":
    case "TURNSTILE_REQUIRED":
      return "Doğrulama tutmadı, tekrar dene.";
    case "NETWORK":
      return "Bağlantı yok gibi. Tekrar dene.";
    case "NOT_FOUND":
      return "Bu bulgu artık ortada yok.";
    default:
      return err.message;
  }
}
